import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Lightning } from "@phosphor-icons/react";
import { F, StaleResultPlaceholder } from "./primitives";
import { MotorBranchInputs } from "./MotorBranchInputs";
import { TransformerInputs } from "./TransformerInputs";
import { MotorGroupInputs } from "./MotorGroupInputs";
import { ConductorResultPanel } from "./ConductorResultPanel";

/* v2.87 · Conductor sizing card — extracted from Calculators.jsx during
   the components/calculators/ refactor. Behaviour unchanged from v2.86;
   sub-input strips and result rendering live in sibling modules. */
export function ConductorSizingCard({ defaults = {}, compact = false }) {
  const [form, setForm] = useState({
    code: "NEC",
    load_amps: 80,
    load_value: 80,          // v2.72 · numeric value in load_unit
    load_unit: "A",          // v2.72 · "A" | "hp" | "kW"
    continuous: true,
    continuous_factor: 1.25, // v2.72 · explicit 1.0 or 1.25
    temp_rating: 75,
    ambient_c: 30,
    parallel_sets: 1,
    voltage: 480,
    phases: 3,
    length_ft: 100,
    power_factor: 0.9,
    cable_type: "THWN-2",
    location_wet: false,
    media: "conduit",
    cables_in_media: 3,
    bundled: true,
    conductor_material: "copper",
    allow_next_size_up: false,  // v2.68 · NEC 240.4(B)
    conduit_material: "",  // v2.69 · "" | EMT | RMC | PVC-40
    include_neutral_in_conduit: true,  // v2.69
    // v2.73 · Circuit-type-specific NEC rules.
    circuit_type: "feeder",                     // feeder | motor_branch | transformer
    motor_protection: "inverse_time_breaker",   // motor branch OCPD type
    motor_service_factor: 1.15,
    transformer_kva: 75,
    transformer_secondary_voltage: 208,
    transformer_side: "primary",
    transformer_has_secondary_protection: false,
    // v2.83 · NEC 450.3(B) Supervised-location exception + both-sides sizing.
    transformer_supervised: false,
    transformer_primary_length_ft: 50,
    transformer_secondary_length_ft: 30,
    transformer_size_both_sides: false,
    // v2.74 · Motor-group feeder — dynamic list of motors sharing a feeder.
    motor_group_motors: [
      { tag: "M-1", hp: 50, voltage: "", phases: "" },
      { tag: "M-2", hp: 25, voltage: "", phases: "" },
    ],
    motor_group_other_loads_amps: 0,
    ...defaults,
  });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [cableTypes, setCableTypes] = useState([]);
  const [lastRun, setLastRun] = useState(null);
  const [hasComputed, setHasComputed] = useState(false);

  // v2.60 · any input change after Compute must invalidate the shown result.
  const patchForm = (patch) => {
    setForm((prev) => ({ ...prev, ...patch }));
    setResult(null);
  };

  useEffect(() => {
    api.get("/calculators/cable-types").then(({ data }) => setCableTypes(data)).catch(() => {});
  }, []);

  const compute = async () => {
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post("/calculators/conductor-sizing", form);
      setResult(data);
      setHasComputed(true);
      if (lastRun) {
        const parts = [];
        if (lastRun.inputs.load_amps !== form.load_amps) {
          parts.push(`load ${lastRun.inputs.load_amps} → ${form.load_amps}A`);
        }
        if (lastRun.result?.selected_size !== data.selected_size) {
          parts.push(`size ${lastRun.result?.selected_size ?? "—"} → ${data.selected_size}`);
        }
        if (parts.length) toast.info(`Recomputed · ${parts.join(" · ")}`);
      }
      setLastRun({ inputs: { ...form }, result: data });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  // v2.71 · Print stampable PE calc sheet — a full-page NEC audit-trail PDF.
  const printPESheet = async () => {
    const circuit_tag = window.prompt(
      "Circuit tag for this calc sheet (e.g. MCC-01-F1). Leave blank to skip.",
      "",
    );
    if (circuit_tag === null) return;
    const engineer_name = window.prompt("Reviewing engineer name (optional):", "") ?? "";
    const engineer_pe_license = window.prompt("PE license # (optional):", "") ?? "";
    setBusy(true);
    try {
      const res = await api.post(
        "/calculators/conductor-sizing/pe-sheet",
        { ...form, circuit_tag: circuit_tag || "—",
          engineer_name, engineer_pe_license, project_name: "—" },
        { responseType: "blob" },
      );
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safe = (circuit_tag || "conductor").replace(/[^a-zA-Z0-9._-]+/g, "_").slice(0, 40);
      a.download = `pe_calc_${safe}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("PE calc sheet downloaded");
    } catch {
      toast.error("Failed to render PE sheet");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-white/10 bg-[#12151C] p-6" data-testid="conductor-sizing-card">
      <div className="flex items-center gap-2 mb-1">
        <Lightning weight="fill" size={18} className="text-[#00E5FF]" />
        <div className="label-tag">// Conductor sizing</div>
      </div>
      <h3 className="font-display text-2xl font-bold">Feeder / branch conductor</h3>
      <p className="text-sm text-gray-400 mt-1">
        Copper or Aluminum, NEC 310.16 or CEC Table 2 — picks smallest conductor that meets load × 1.25 (continuous) + ambient + voltage-drop.
      </p>

      <div className={`mt-5 grid ${compact ? "grid-cols-2" : "grid-cols-2 md:grid-cols-3"} gap-3`}>
        <F label="Conductor material">
          <select value={form.conductor_material} onChange={(e) => patchForm({ conductor_material: e.target.value })}
            className="w-full bg-black/40 border border-white/10 px-2 py-2 text-sm font-mono focus:border-[#00E5FF] outline-none"
            data-testid="cond-material">
            <option value="copper">Copper (Cu)</option>
            <option value="aluminum">Aluminum (Al)</option>
          </select>
        </F>
        <F label="Code">
          <select value={form.code} onChange={(e) => patchForm({ code: e.target.value })}
            className="w-full bg-black/40 border border-white/10 px-2 py-2 text-sm font-mono focus:border-[#00E5FF] outline-none"
            data-testid="cond-code">
            <option value="NEC">NEC (US)</option>
            <option value="CEC">CEC (Canada)</option>
          </select>
        </F>
        <F label="Circuit type">
          <select value={form.circuit_type}
            onChange={(e) => patchForm({ circuit_type: e.target.value })}
            className="w-full bg-black/40 border border-white/10 px-2 py-2 text-sm font-mono focus:border-[#00E5FF] outline-none"
            data-testid="cond-circuit-type"
            title="Feeder = generic feeder / branch (NEC 215.2 / 240.4). Motor branch adds NEC 430.52 OCPD sizing + 430.32 overload relay window. Transformer adds NEC 450.3(B) primary/secondary FLC + OCPD sizing.">
            <option value="feeder">Feeder / branch</option>
            <option value="motor_branch">Motor branch (NEC 430)</option>
            <option value="motor_group">Motor group feeder (NEC 430.24)</option>
            <option value="transformer">Transformer (NEC 450)</option>
          </select>
        </F>
        <F label={`Load${form.load_unit !== "A" ? ` (→ FLA / calc)` : ""}`}>
          <div className="flex gap-1">
            <input type="number" value={form.load_value}
              onChange={(e) => patchForm({
                load_value: Number(e.target.value),
                load_amps: form.load_unit === "A" ? Number(e.target.value) : form.load_amps,
              })}
              className="w-full bg-black/40 border border-white/10 px-2 py-2 text-sm font-mono focus:border-[#00E5FF] outline-none"
              data-testid="cond-amps" />
            <select value={form.load_unit}
              onChange={(e) => patchForm({ load_unit: e.target.value })}
              className="bg-black/40 border border-white/10 px-2 py-2 text-xs font-mono focus:border-[#00E5FF] outline-none"
              data-testid="cond-load-unit"
              title="A = amperes (direct). hp = NEC Tables 430.247/248/250 motor FLA lookup. kW = theoretical conversion using I = 1000·kW/(V·√3·PF) for 3φ, I = 1000·kW/(V·PF) for 1φ, I = 1000·kW/V for DC.">
              <option value="A">A</option>
              <option value="hp">hp</option>
              <option value="kW">kW</option>
            </select>
          </div>
          {result?.input?.load_conversion_note && form.load_unit !== "A" && (
            <div className="text-[10px] text-[#00E5FF]/70 mt-1 font-mono"
                 data-testid="cond-load-note">
              → {result.input.load_amps.toFixed(1)} A
            </div>
          )}
        </F>
        <F label="Term. temp °C">
          <select value={form.temp_rating} onChange={(e) => patchForm({ temp_rating: Number(e.target.value) })}
            className="w-full bg-black/40 border border-white/10 px-2 py-2 text-sm font-mono focus:border-[#00E5FF] outline-none"
            data-testid="cond-temp">
            <option value={60}>60°C</option>
            <option value={75}>75°C</option>
            <option value={90}>90°C</option>
          </select>
        </F>
        <F label="Ambient °C">
          <input type="number" value={form.ambient_c} onChange={(e) => patchForm({ ambient_c: Number(e.target.value) })}
            className="w-full bg-black/40 border border-white/10 px-2 py-2 text-sm font-mono focus:border-[#00E5FF] outline-none"
            data-testid="cond-ambient" />
        </F>
        <F label="Voltage V">
          <input type="number" value={form.voltage} onChange={(e) => patchForm({ voltage: Number(e.target.value) })}
            className="w-full bg-black/40 border border-white/10 px-2 py-2 text-sm font-mono focus:border-[#00E5FF] outline-none"
            data-testid="cond-voltage" />
        </F>
        <F label="Phases">
          <select value={form.phases} onChange={(e) => patchForm({ phases: Number(e.target.value) })}
            className="w-full bg-black/40 border border-white/10 px-2 py-2 text-sm font-mono focus:border-[#00E5FF] outline-none"
            data-testid="cond-phases"
            title="AC circuits use NEC Chapter 9 Table 9 (Zeff = R·cos φ + X·sin φ, includes reactance). DC uses Table 8 (round-trip R only).">
            <option value={0}>DC</option>
            <option value={1}>1φ AC</option>
            <option value={3}>3φ AC</option>
          </select>
        </F>
        <F label="Length ft">
          <input type="number" value={form.length_ft} onChange={(e) => patchForm({ length_ft: Number(e.target.value) })}
            className="w-full bg-black/40 border border-white/10 px-2 py-2 text-sm font-mono focus:border-[#00E5FF] outline-none"
            data-testid="cond-length" />
        </F>
        <F label="Parallel sets">
          <input type="number" min={1} value={form.parallel_sets} onChange={(e) => patchForm({ parallel_sets: Number(e.target.value) })}
            className="w-full bg-black/40 border border-white/10 px-2 py-2 text-sm font-mono focus:border-[#00E5FF] outline-none"
            data-testid="cond-parallel" />
        </F>
        <F label="Cable type">
          <select value={form.cable_type} onChange={(e) => patchForm({ cable_type: e.target.value })}
            className="w-full bg-black/40 border border-white/10 px-2 py-2 text-sm font-mono focus:border-[#00E5FF] outline-none"
            data-testid="cond-cable-type">
            {cableTypes.length === 0 && <option value="THWN-2">THWN-2</option>}
            {cableTypes.map((c) => (
              <option key={c.key} value={c.key}>{c.key} — {c.temp_c}°C{c.wet ? " · wet OK" : ""}</option>
            ))}
          </select>
        </F>
        <F label="Media">
          <select value={form.media} onChange={(e) => patchForm({ media: e.target.value })}
            className="w-full bg-black/40 border border-white/10 px-2 py-2 text-sm font-mono focus:border-[#00E5FF] outline-none"
            data-testid="cond-media">
            <option value="free_air">Free air</option>
            <option value="tray">Cable tray</option>
            <option value="conduit">Conduit / raceway</option>
          </select>
        </F>
        <F label="Cables / wires in media">
          <input type="number" min={1} value={form.cables_in_media}
            onChange={(e) => patchForm({ cables_in_media: Number(e.target.value) })}
            className="w-full bg-black/40 border border-white/10 px-2 py-2 text-sm font-mono focus:border-[#00E5FF] outline-none"
            data-testid="cond-cables-in-media" />
        </F>
        <F label="Arrangement">
          <select value={form.bundled ? "bundled" : "spaced"}
            onChange={(e) => patchForm({ bundled: e.target.value === "bundled" })}
            disabled={form.media === "conduit"}
            className="w-full bg-black/40 border border-white/10 px-2 py-2 text-sm font-mono focus:border-[#00E5FF] outline-none disabled:opacity-50"
            data-testid="cond-bundled"
            title={form.media === "conduit" ? "Conduit always treated as bundled per NEC 310.15(C)(1)" : ""}>
            <option value="bundled">Bundled / stacked</option>
            <option value="spaced">Spaced ≥ 1 OD</option>
          </select>
        </F>
        <F label="Conduit fill (opt-in)">
          <select value={form.conduit_material}
            onChange={(e) => patchForm({ conduit_material: e.target.value })}
            className="w-full bg-black/40 border border-white/10 px-2 py-2 text-sm font-mono focus:border-[#00E5FF] outline-none"
            data-testid="cond-conduit-material"
            title="Pick a raceway material to also compute the smallest legal conduit trade size per NEC Chapter 9 Table 4 & 5 (plus a 250.122-sized EGC). Leave blank to skip conduit sizing.">
            <option value="">— skip —</option>
            <option value="EMT">EMT (thin-wall)</option>
            <option value="RMC">RMC (rigid metal)</option>
            <option value="PVC-40">PVC Schedule 40</option>
          </select>
        </F>
        <label className="flex items-center gap-2 mt-6"
               title="NEC 210.19(A)(1) / 215.2(A)(1): a load expected to run at max current for 3+ hours must be sized at 125% of its rated amps. Pick 1.25 for continuous, 1.00 for non-continuous.">
          <span className="text-sm">Continuous factor</span>
          <select value={form.continuous_factor}
            onChange={(e) => patchForm({
              continuous_factor: Number(e.target.value),
              continuous: Number(e.target.value) >= 1.25,
            })}
            className="bg-black/40 border border-white/10 px-2 py-1 text-xs font-mono focus:border-[#00E5FF] outline-none"
            data-testid="cond-continuous">
            <option value={1.0}>1.00</option>
            <option value={1.25}>1.25</option>
          </select>
        </label>
        <label className="flex items-center gap-2 mt-6">
          <input type="checkbox" checked={form.location_wet} onChange={(e) => patchForm({ location_wet: e.target.checked })}
            className="accent-[#00E5FF]"
            data-testid="cond-wet" />
          <span className="text-sm">Wet location</span>
        </label>
        <label className="flex items-center gap-2 mt-6"
               title="NEC 240.4(B): when the OCPD (≤ 800 A) sized to the load × 1.25 is one standard rating above the cable's ampacity, the code permits keeping the smaller cable rather than upsizing. Not permitted on branch circuits with multiple receptacles — that's your call as PE.">
          <input type="checkbox" checked={form.allow_next_size_up}
            onChange={(e) => patchForm({ allow_next_size_up: e.target.checked })}
            className="accent-[#00E5FF]"
            data-testid="cond-next-size-up" />
          <span className="text-sm">240.4(B) exception</span>
          <span className="text-[10px] text-gray-500 font-mono ml-1">≤ 800 A</span>
        </label>
      </div>

      {form.circuit_type === "motor_branch" && (
        <MotorBranchInputs form={form} patchForm={patchForm} />
      )}
      {form.circuit_type === "transformer" && (
        <TransformerInputs form={form} patchForm={patchForm} />
      )}
      {form.circuit_type === "motor_group" && (
        <MotorGroupInputs form={form} patchForm={patchForm} />
      )}

      <div className="flex flex-wrap gap-3 mt-5">
        <button onClick={compute} disabled={busy} className="btn-primary" data-testid="cond-compute">
          {busy ? "Computing…" : "Compute"}
        </button>
        <button
          onClick={printPESheet}
          disabled={busy || !result}
          className="border border-[#00E5FF]/40 text-[#00E5FF] px-4 py-2 text-sm font-mono hover:bg-[#00E5FF]/10 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          data-testid="cond-print-pe-sheet"
          title={result
            ? "Download a full NEC audit-trail PDF ready for a licensed PE to stamp."
            : "Compute first — the PE sheet always reflects the current live result, never stale numbers."}>
          Print PE calc sheet ↓
        </button>
      </div>

      {result && <ConductorResultPanel result={result} />}
      {!result && hasComputed && (
        <StaleResultPlaceholder tone="cyan" testid="cond-result-stale" />
      )}
    </div>
  );
}
